"use client"

import { useNavigate } from "react-router-dom"
import { useApp } from "../../contexts/AppContext"
import { useState } from "react"
import { BiPencil, BiTrash, BiFolder, BiRightArrow } from "react-icons/bi"
import { motion, AnimatePresence } from "framer-motion"

import ClasseurModal from "../modals/ClasseurModal"
import ConfirmAlert from "../alerts/ConfirmAlert"
import DropdownOption from "../ui/DropdownOption"
import Badge from "../ui/Badge"


export default function ClasseurCard({ classeur }) {
  const navigate = useNavigate()

  const { getFeuillesByClasseur, deleteClasseurAPI, loading } = useApp()
  const feuilles = getFeuillesByClasseur(classeur.code_classeur)

  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const handleDelete = async () => {
    try {
      await deleteClasseurAPI(classeur.code_classeur)
    } catch (error) {
      console.error("Erreur lors de la suppression:", error)
    }
  }

  const handleSelectClasseur = async (classeur) => {
    localStorage.setItem("selec-classeur", classeur.code_classeur)
    navigate(`/classeur/${classeur.code_classeur}`)
  }

  const dropdownOptions = [
    {
      label: "Modifier",
      icon: <BiPencil />,
      onClick: () => setShowEditModal(true),
      className: "text-primary",
    },
    {
      label: "Supprimer",
      icon: <BiTrash />,
      onClick: () => setShowDeleteConfirm(true),
      className: "text-danger",
    },
  ]

  // Variants pour les animations
  const cardVariants = {
    initial: {
      rotateY: 0,
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    hover: {
      rotateY: 2,
      boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
  }

  const badgeVariants = {
    initial: { scale: 1, y: 0 },
    hover: {
      scale: 1.05,
      y: -2,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10,
      },
    },
  }

  const footerVariants = {
    initial: { opacity: 0.7 },
    hover: {
      opacity: 1,
      transition: {
        duration: 0.2,
      },
    },
  }

  // Fonction utilitaire pour ajuster la luminosité
  function adjustBrightness(hex, percent) {
    const num = Number.parseInt(hex.replace("#", ""), 16)
    const amt = Math.round(2.55 * percent)
    const R = (num >> 16) + amt
    const G = ((num >> 8) & 0x00ff) + amt
    const B = (num & 0x0000ff) + amt
    return (
      "#" +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    )
  }

  return (
    <>
      <motion.div
        variants={cardVariants}
        initial="initial"
        whileHover="hover"
        whileTap="tap"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        className="card h-100 bordercard h-100 shadow-sm border rounded-1"
        style={{ cursor: "pointer", transformStyle: "preserve-3d" }}
      >
        {/* En-tête avec couleur et animation */}
        <motion.div
          initial={{ background: classeur.couleur_classeur || "#6c757d", }}
          hover={{
            background: `linear-gradient(45deg, ${classeur.couleur_classeur || "#6c757d"}, ${adjustBrightness(classeur.couleur_classeur || "#6c757d", 20)})`,
            transition: { duration: 0.3 },
          }}
          className="card-header text-white d-flex justify-content-between align-items-center border-bottom py-1">
          <motion.h6 className="card-title mb-0">
            <motion.span
              initial={{ scale: 1 }}
              hover={{ scale: 1.1, transition: { duration: 0.5 } }}
            > <BiFolder className="fs-5 me-1 mb-1" />
            </motion.span>

            <motion.span initial={{ x: 0 }} animate={isHovered ? { x: 5 } : { x: 0 }} transition={{ type: "spring", stiffness: 300 }}>
              {classeur.libelle_classeur}
            </motion.span>
          </motion.h6>

          <motion.div initial={{ opacity: 0.8 }} animate={isHovered ? { opacity: 1 } : { opacity: 0.8 }}>
            <DropdownOption options={dropdownOptions} />
          </motion.div>
        </motion.div>

        {/* Corps de la carte */}
        <motion.div className="card-body" initial={{ opacity: 1 }} animate={isHovered ? { opacity: 1 } : { opacity: 0.95 }}>
          <AnimatePresence>
            {classeur.note_classeur && (
              <motion.p
                className="card-text text-muted small mb-3"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}>
                {classeur.note_classeur}
              </motion.p>
            )}
          </AnimatePresence>

          <div className="d-flex justify-content-between align-items-center">
            <motion.span variants={badgeVariants} className="badge bg-secondary rounded-1">
              {feuilles.length} feuille{feuilles.length !== 1 ? "s" : ""}
            </motion.span>

            {classeur.id_projet && (
              <motion.span variants={badgeVariants} className="badge bg-info rounded-1">
                Projet {classeur.id_projet}
              </motion.span>
            )}
          </div>
        </motion.div>

        {/* Pied de carte avec animation */}
        <motion.div variants={footerVariants} className="card-footer bg-transparent border-top py-1">
          <button onClick={() => handleSelectClasseur(classeur)} className="btn btn-sm btn-link link-primary text-decoration-none text-muted w-100">
            <small className="d-flex align-items-center justify-content-center">
              <BiRightArrow className="me-1" />
              Cliquer pour ouvrir
            </small>
          </button>
        </motion.div>

        {/* Effet de brillance au survol */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0, x: "-100%" }}
              animate={{ opacity: 1, x: "100%" }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
          )}
        </AnimatePresence>

        {/* Indicateur de chargement */}
        {loading && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75 rounded">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
          </div>
        )}
      </motion.div>

      {/* Modals avec animations */}
      <AnimatePresence>
        {showEditModal && (
          <ClasseurModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            projectId={classeur.id_projet}
            classeurId={classeur.code_classeur}
          />
        )}
      </AnimatePresence>

      <ConfirmAlert
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Supprimer le classeur"
        message={`Êtes-vous sûr de vouloir supprimer le classeur "${classeur.libelle_classeur}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        variant="danger"
      />
    </>
  )
}
